/* eslint-disable react-hooks/exhaustive-deps */
import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import { editInstruction2 } from "services/doubtfulService";
import { DoubtfulCase } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  instruction: DoubtfulCase;
  onClose: (refetch: boolean) => void;
};

const GivingTeamDecisionForm: FC<Props> = ({ instruction, onClose }) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const [formState, setFormState] = useState<DoubtfulCase>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (instruction) {
      setFormState({ ...formState, id: instruction?.id, ...instruction });
    }
  }, [instruction]);

  // team_final_instruction_summary_attchement_info
  // team_resolution_summary_document_info
  // team_final_instruction_date

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.team_final_instruction_date)
      return t("giving_team_decision_team_final_instruction_date_required");
    else if (!formState.team_final_instruction_summary)
      return t("giving_team_decision_team_final_instruction_summary_required");
    else if (!formState.team_final_instruction_summary_attchement)
      return t(
        "giving_team_decision_team_final_instruction_summary_attachment_required",
      );
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    editInstruction2({
      ...formState,
      id: formState.id,
      request_status: "forwarded_to_cordinator",
      giving_direction_office_admin_unit: loginData?.admin_unit,
      giving_direction_office_org_unit: loginData?.org_unit,
    })
      .then(() => {
        showTooltip(
          instruction?.request_status === "Rejected"
            ? t("successfully_updated")
            : t("successfully_submitted"),
          "success",
        );
        setFormState({});
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleFile = (file: File | null, name: string) => {
    setFormState({ ...formState, [name]: file });
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {instruction?.request_status === "Rejected"
            ? t("update_decision")
            : `${t("add")} ${t("decision")}`}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("asking_team_decision_subtitle")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-2 gap-x-4 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("giving_team_decision_team_final_instruction_date")}
              </label>
              <input
                type="date"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="team_final_instruction_date"
                value={
                  formState?.team_final_instruction_date
                    ? new Date(formState.team_final_instruction_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("giving_team_decision_team_final_instruction_summary")}
              </label>
              <textarea
                rows={4}
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="team_final_instruction_summary"
                value={formState?.team_final_instruction_summary || ""}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <FileUpload
                label={t(
                  "giving_team_decision_team_final_instruction_summary_attachment",
                )}
                fileTypes="application/pdf,image/*"
                info={t("giving_team_decision_upload_summary_document")}
                required={true}
                onChange={(file) =>
                  handleFile(file, "team_final_instruction_summary_attchement")
                }
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                                  text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                                          ${
                                            loading
                                              ? "opacity-50 cursor-not-allowed"
                                              : ""
                                          }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : (
                <>
                  {instruction?.request_status === "Rejected"
                    ? t("update_decision")
                    : `${t("add")} ${t("decision")}`}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GivingTeamDecisionForm;
